import { Link } from "@inertiajs/react"
import { ArrowLeft, Eye, Pencil, Star, User } from "lucide-react"

import AppShell from "@/components/tuw/AppShell"
import { Avatar } from "@/components/tuw/Avatar"
import { initialsFor } from "@/components/tuw/hire/FreelancerAvatar"
import StarRating from "@/components/tuw/hire/StarRating"
import { Button } from "@/components/ui/button"

// AT-01 — the customer Public Profile: a standalone read-only preview of what
// freelancers see when they open this customer's profile (design:
// design/spec/account/public-profile.html). Everything inside the dashed
// "Visible to freelancers" frame mirrors the legacy freelancer-facing view;
// private details (email, rewards, referral, billing) never reach this payload.
// Editing stays on /profile — this page only points there.

// "Member since March 2024" — full month + year in the identity header.
function monthYearLong(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString("en-US", { year: "numeric", month: "long" })
}

// Review rows date as "Jun 2026" (short month), matching the design.
function monthYearShort(iso) {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short" })
}

function PreviewBanner() {
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-border bg-secondary px-[18px] py-3.5 sm:flex-nowrap">
      <span className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-border bg-card text-muted-foreground">
        <Eye className="size-[18px]" strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="font-display text-sm font-bold text-foreground">
          This is how freelancers see your profile
        </h2>
        <p className="mt-0.5 text-[12.5px] leading-normal text-muted-foreground">
          A read-only preview of your marketplace profile. Private details — email, rewards,
          billing — are never shown to freelancers.
        </p>
      </div>
      <div className="w-full shrink-0 sm:w-auto">
        <Button variant="outline" size="sm" asChild className="w-full sm:w-auto">
          <Link href="/profile">
            <ArrowLeft className="size-4" strokeWidth={2} />
            Back to Profile
          </Link>
        </Button>
      </div>
    </div>
  )
}

// The dashed frame + floating tag marking exactly what freelancers can see.
function VisibleFrame({ children }) {
  return (
    <div className="relative flex flex-col gap-4 rounded-[22px] border-[1.5px] border-dashed border-line-2 p-2.5 sm:p-3.5">
      <span className="absolute -top-[9px] left-[22px] inline-flex items-center gap-[5px] bg-background px-[9px] text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">
        <Eye className="size-3" strokeWidth={2.1} />
        Visible to freelancers
      </span>
      {children}
    </div>
  )
}

function StatTile({ value, label }) {
  return (
    <div className="min-w-[86px] flex-1 rounded-[11px] bg-secondary px-4 py-[11px] text-center md:flex-none">
      <div className="font-display text-[19px] font-bold tracking-[-0.4px] text-foreground">
        {value}
      </div>
      <div className="mt-0.5 text-[11px] font-semibold text-ink-3">{label}</div>
    </div>
  )
}

function IdentityCard({ identity, stats }) {
  const since = monthYearLong(identity?.memberSince)
  const subline = [
    identity?.username ? `@${identity.username}` : null,
    since ? `Member since ${since}` : null,
  ]
    .filter(Boolean)
    .join(" · ")

  return (
    <div className="flex flex-wrap items-center gap-5 rounded-2xl border border-border bg-card px-6 py-[22px] md:flex-nowrap">
      <Avatar
        initials={identity?.initials ?? "?"}
        name={identity?.name}
        src={identity?.avatarUrl}
        size={72}
        me
      />
      <div className="min-w-0 flex-1">
        <h1 className="font-display text-[23px] font-bold tracking-[-0.5px] text-foreground">
          {identity?.name}
        </h1>
        {subline && <p className="mt-px text-[13px] text-ink-3">{subline}</p>}
        <div className="mt-2.5 flex items-center gap-[9px]">
          {identity?.rating != null ? (
            <>
              <StarRating rating={identity.rating} showCount={false} />
              <span className="text-[12.5px] font-medium text-ink-3">
                {identity.ratingsCount}{" "}
                {identity.ratingsCount === 1 ? "rating" : "ratings"} from freelancers
              </span>
            </>
          ) : (
            <span className="text-[12.5px] font-medium text-ink-3">No ratings yet</span>
          )}
        </div>
      </div>
      <div className="flex w-full shrink-0 gap-2.5 md:w-auto">
        <StatTile value={stats?.orders ?? 0} label="Orders" />
        <StatTile value={stats?.revisions ?? 0} label="Revisions" />
      </div>
    </div>
  )
}

function CardHead({ icon: Icon, title, right }) {
  return (
    <div className="flex items-center justify-between gap-2.5 border-b border-border px-5 py-[15px]">
      <h3 className="flex items-center gap-[9px] font-display text-[14.5px] font-bold text-foreground">
        <Icon className="size-[17px] text-primary" strokeWidth={1.9} />
        {title}
      </h3>
      {right}
    </div>
  )
}

function EmptyBlock({ icon: Icon, title, body, action }) {
  return (
    <div className="flex flex-col items-center px-5 py-[30px] text-center">
      <span className="mb-[11px] grid size-[42px] place-items-center rounded-xl bg-secondary text-ink-3">
        <Icon className="size-5" strokeWidth={1.8} />
      </span>
      <h4 className="font-display text-[13.5px] font-bold text-foreground">{title}</h4>
      <p className="mx-auto mt-[5px] max-w-[44ch] text-[12.5px] leading-[1.55] text-ink-3">
        {body}
      </p>
      {action}
    </div>
  )
}

// The freelancer-facing "Get To Know Me" answers. Empty → point the customer
// at Profile to add them (editing stays on Profile, never inline here).
function AboutCard({ about }) {
  const empty = about.length === 0

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <CardHead
        icon={User}
        title="About me"
        right={
          !empty && (
            <Link
              href="/profile"
              className="text-xs font-semibold text-muted-foreground hover:underline"
            >
              Edit in Profile
            </Link>
          )
        }
      />
      {empty ? (
        <EmptyBlock
          icon={User}
          title="No About me yet"
          body="Freelancers read this when deciding to accept your invites. Add a few lines about you and what you publish."
          action={
            <Button variant="outline" size="sm" asChild className="mt-3.5">
              <Link href="/profile">
                <Pencil className="size-4" strokeWidth={2} />
                Add it in Profile
              </Link>
            </Button>
          }
        />
      ) : (
        <div className="flex flex-col px-5 py-[18px]">
          {about.map((q) => (
            <div
              key={q.key}
              className="border-b border-border py-3.5 first:pt-0 last:border-none last:pb-0"
            >
              <div className="mb-[5px] text-[12.5px] font-bold text-ink-3">{q.label}</div>
              <p className="max-w-[64ch] text-[13.5px] leading-[1.7] text-foreground">
                {q.answer}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ReviewRow({ review }) {
  const date = monthYearShort(review.date)

  return (
    <div className="border-b border-border px-5 py-4 last:border-b-0">
      <div className="flex flex-wrap items-center gap-[11px] sm:flex-nowrap">
        <Avatar initials={initialsFor(review.by)} name={review.by} size={38} />
        <div className="min-w-0">
          <div className="font-display text-[13.5px] font-bold text-foreground">{review.by}</div>
          <div className="mt-px flex items-center gap-[5px] text-xs text-ink-3">
            {review.role && (
              <span className="inline-flex rounded-full bg-secondary px-2 py-px text-[10.5px] font-bold text-muted-foreground">
                {review.role}
              </span>
            )}
            {date && <span>{date}</span>}
          </div>
        </div>
        <div className="hidden flex-1 sm:block" />
        {review.rating != null && (
          <StarRating
            rating={review.rating}
            showCount={false}
            className="mt-1 w-full pl-[49px] sm:mt-0 sm:w-auto sm:pl-0"
          />
        )}
      </div>
      {review.comment && (
        <div className="mt-2.5 text-[13px] leading-[1.65] whitespace-pre-line text-muted-foreground">
          {review.comment}
        </div>
      )}
    </div>
  )
}

// Reviews freelancers left for this customer — the same visible set the legacy
// freelancer view renders.
function ReviewsCard({ reviews, rating }) {
  const empty = reviews.length === 0

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card">
      <CardHead
        icon={Star}
        title="Reviews from freelancers"
        right={
          !empty && (
            <span className="flex items-center gap-2 text-xs text-ink-3">
              {rating != null && <StarRating rating={rating} showCount={false} />}
              <span>
                · {reviews.length} {reviews.length === 1 ? "review" : "reviews"}
              </span>
            </span>
          )
        }
      />
      {empty ? (
        <EmptyBlock
          icon={Star}
          title="No reviews yet"
          body="After an order wraps, your freelancers can rate the experience of working with you. Reviews show up here."
        />
      ) : (
        reviews.map((review) => <ReviewRow key={review.id} review={review} />)
      )}
    </div>
  )
}

export default function PublicProfile({ publicProfile }) {
  const identity = publicProfile?.identity ?? {}
  const stats = publicProfile?.stats ?? {}
  const about = publicProfile?.about ?? []
  const reviews = publicProfile?.reviews ?? []

  return (
    <div className="mx-auto flex w-full max-w-[860px] flex-col gap-[18px] px-4 pt-[18px] pb-[52px] sm:px-[30px] sm:pt-[26px] sm:pb-[60px]">
      <PreviewBanner />
      <VisibleFrame>
        <IdentityCard identity={identity} stats={stats} />
        <AboutCard about={about} />
        <ReviewsCard reviews={reviews} rating={identity.rating} />
      </VisibleFrame>
    </div>
  )
}

// `active="Profile"` matches no NAV key — the sidebar-foot account block
// self-activates on /profile (same note as Profile.jsx).
PublicProfile.layout = (page) => (
  <AppShell active="Profile" crumb={["Account", "Profile", "Public profile"]}>
    {page}
  </AppShell>
)
